import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import renderSelectField from '../../../../shared/components/form/Select';
import Tooltip from '../../../../shared/components/widgets/Tooltip';
import { renderTextArea } from '../../shared/formComponents';
import {
  BRAND_AD_FORMAT_OPTIONS,
  ITEM_TYPE_OPTIONS,
  ITEM_SELECTION_OPTIONS,
  DESTINATION_OPTIONS,
} from '../../shared/constants';
import config from '../../../../config';

const BrandBudgetFields = ({
  setupFlow,
  agencyInternal,
  masterItemOnList,
  currentEditRow,
  selectedSeller,
  user,
  dispatch,
  change,
  itemCsvInput,
}) => {
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);

  // Fetch brands from API
  useEffect(() => {
    if (selectedSeller.value && brands.length === 0 && !loadingBrands) {
      setLoadingBrands(true);
      const accountId = selectedSeller.value === 124 ? 601 : selectedSeller.value; // Force 601 for test account 124
      const fetchUrl = `${config.serverUrl}/customers/brands?accountId=${accountId}`;
      const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

      fetch(fetchUrl, { headers: headerWithAuth })
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to fetch brands');
          }
          return response.json();
        })
        .then(data => {
          const brandOptions = data.map(brand => ({
            label: brand.brandRegistryName,
            value: brand.brandId,
            brandEntityId: brand.brandEntityId,
          }));
          setBrands(brandOptions);
          setLoadingBrands(false);

          // If editing an existing brand budget, set the brand dropdown value
          if (currentEditRow) {
            const currentBudgetTypeForBrand = currentEditRow.budget_type || currentEditRow.budget_item_type;
            if (currentBudgetTypeForBrand === 'brand' && currentEditRow.brand_entity_id) {
              const matchingBrand = brandOptions.find(b => b.brandEntityId === currentEditRow.brand_entity_id);
              if (matchingBrand) {
                dispatch(change('budgetGroupEditModal', 'brand', matchingBrand));
              }
            }
          }
        })
        .catch(error => {
          console.error('Error fetching brands:', error);
          setLoadingBrands(false);
        });
    }
  }, [selectedSeller.value, brands.length, loadingBrands, user.jwtToken, currentEditRow, dispatch, change]);

  return (
    <>
      <div className="form__form-group">
        <span className="form__form-group-label">Brand <Tooltip id="brand" msg="Select the brand for this budget group" placement="top" /></span>
        <div className="form__form-group-field">
          {loadingBrands ? (
            <div>Loading brands...</div>
          ) : (
            <Field
              name="brand"
              type="text"
              component={renderSelectField}
              options={brands}
              onChange={(value) => {
                if (value) {
                  dispatch(change('budgetGroupEditModal', 'brandName', value.label));
                  dispatch(change('budgetGroupEditModal', 'brandEntityId', value.brandEntityId));
                }
              }}
            />
          )}
        </div>
      </div>

      <div className="form__form-group">
        <span className="form__form-group-label">Ad Format <Tooltip id="brandAdFormat" msg="Select the ad format for this brand budget" placement="top" /></span>
        <div className="form__form-group-field">
          <Field
            name="brandAdFormat"
            type="text"
            component={renderSelectField}
            options={BRAND_AD_FORMAT_OPTIONS}
          />
        </div>
      </div>

      <div className="form__form-group">
        <span className="form__form-group-label">
          Allowed ASINs (<a href="/self-service/budget#Items" target="_blank" rel="noopener noreferrer">View Catalog HERE</a>)
          {' '}<Tooltip id="masterItemOnList" msg="List of ASINs allowed for this brand budget (3-100 required)" placement="top" />
        </span>
        <div className="form__form-group-field">
          <Field
            name="masterItemOnList"
            type="text"
            component={renderTextArea}
            format={itemCsvInput}
          />
        </div>
        {masterItemOnList && (() => {
          const itemList = (masterItemOnList || '').split(',').map(item => item.trim()).filter(item => item);
          const itemCount = itemList.length;
          const upperCaseItems = itemList.map(item => item.toUpperCase());
          const hasDuplicates = new Set(upperCaseItems).size !== itemList.length;

          const invalidAsins = itemList.filter(item => !item?.toUpperCase().startsWith('B0') || item?.length !== 10);
          const isCountValid = itemCount >= 3 && itemCount <= 100;
          const isFormatValid = invalidAsins.length === 0;
          const isValid = isCountValid && isFormatValid && !hasDuplicates;

          return (
            <div style={{ marginTop: '10px', padding: '10px', borderRadius: '4px', border: `1px solid ${isValid ? 'rgba(70, 187, 253, 0.4)' : 'rgba(255, 0, 0, 0.4)'}`, backgroundColor: `${isValid ? 'rgba(70, 187, 253, 0.1)' : 'rgba(255, 0, 0, 0.1)'}` }}>
              <div style={{ fontWeight: '500', color: isValid ? '#70bbfd' : '#ff4861' }}>
                {isValid ? '✓' : '⚠'} ASIN Count: {itemCount}
              </div>
              {!isCountValid && (
                <div style={{ fontSize: '0.9em', color: '#721c24', marginTop: '4px' }}>
                  Brand budgets require 3-100 ASINs
                </div>
              )}
              {invalidAsins.length > 0 && (
                <div style={{ fontSize: '0.9em', color: '#721c24', marginTop: '4px' }}>
                  Invalid ASINs (must start with B0 and be 10 characters): {invalidAsins.join(', ')}
                </div>
              )}
              {hasDuplicates && (
                <div style={{ fontSize: '0.9em', color: '#721c24', marginTop: '4px' }}>
                  ⚠ Duplicate ASINs found in list
                </div>
              )}
              {isValid && (
                <div style={{ fontSize: '0.9em', color: '#4a5568', marginTop: '4px' }}>
                  All ASINs are valid ✓
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {setupFlow?.value === 'advanced' && agencyInternal?.value && (
        <div className="form__form-group">
          <span className="form__form-group-label">Item Type</span>
          <div className="form__form-group-field">
            <Field
              name="itemType"
              type="text"
              component={renderSelectField}
              options={ITEM_TYPE_OPTIONS}
            />
          </div>
        </div>
      )}

      {setupFlow?.value === 'advanced' && agencyInternal?.value && (
        <div className="form__form-group">
          <span className="form__form-group-label">Item Selection</span>
          <div className="form__form-group-field">
            <Field
              name="itemSelection"
              type="text"
              component={renderSelectField}
              options={ITEM_SELECTION_OPTIONS}
            />
          </div>
        </div>
      )}

      {setupFlow?.value === 'advanced' && agencyInternal?.value && (
        <div className="form__form-group">
          <span className="form__form-group-label">Destination <Tooltip id="destination" msg="Describes where we are going to take ad traffic (ON or OFF)" placement="top" /></span>
          <div className="form__form-group-field">
            <Field
              name="destination"
              type="text"
              component={renderSelectField}
              options={DESTINATION_OPTIONS}
            />
          </div>
        </div>
      )}
    </>
  );
};

BrandBudgetFields.propTypes = {
  setupFlow: PropTypes.shape(),
  agencyInternal: PropTypes.shape(),
  masterItemOnList: PropTypes.string,
  currentEditRow: PropTypes.shape(),
  selectedSeller: PropTypes.shape().isRequired,
  user: PropTypes.shape().isRequired,
  dispatch: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  itemCsvInput: PropTypes.func.isRequired,
};

BrandBudgetFields.defaultProps = {
  setupFlow: null,
  agencyInternal: null,
  masterItemOnList: '',
  currentEditRow: null,
};

export default BrandBudgetFields;
